import { type AxiosInstance, type CancelToken } from 'axios'

type OtherEmissionFactorsParams = {
  query: string
  category: number
  unit: string
  page: number
  signal: AbortSignal
}
interface EmissionFactor {
  sector: string
  unit: string
  source: string
}
type updateDetailPayload = {
  activity_id: string
  source: number
  region: string
  sector: string
  emission_unit: string
  unit: number
  pollutant: string
  method: string
  factor: string
  description: string
  year: number
  calculation_method: string
}
type UpdatedSourcePayload = {
  canViewActionButtons?: boolean
  common_identifier?: string
  created_by_super_typewriter?: boolean
  source_code: string
  source_id: number
  source_name: string
  source_url?: string
}
type SourcePayload = {
  source_code: string
  source_name: string
  source_url: string
}

type EmissionFactorPayload = EmissionFactor[]

type tokenData = {
  cancelToken: CancelToken | undefined
}

type RegionData = {
  region_code: string
  region_name: string
}

export default (axios: AxiosInstance) => ({
  async relevantEmissionfactorsByCategory(
    categoryId: number,
    pageSize: number,
    extraParams: {
      approvalIds?: number[]
      isProcessed?: boolean
      unitId?: number
      submissionId?: number
    } = {},
  ) {
    let getURL = `/v1/efdb/relevant-emissionfactors/category/${categoryId}/grouped/?page_size=${pageSize}`

    if ((extraParams?.approvalIds || []).length !== 0) {
      getURL += `&query_engine=false&approvals=${extraParams.approvalIds}`
    }
    else if (extraParams?.isProcessed) {
      getURL += `&query_engine=false`
    }
    else {
      getURL += `&query_engine=true`
    }

    if (extraParams?.unitId) {
      getURL += `&unit_id=${extraParams.unitId}`
    }

    if (extraParams?.submissionId) {
      getURL += `&submission_id=${extraParams.submissionId}`
    }

    const { data } = await axios.get(getURL)
    return data
  },
  relevantEmissionFactors(categoryId: number, params?: RelevantEmissionFactorsQueryParams) {
    return axios.get<PaginatedResponseWithEfValidation<GhgEmissionFactorMatchingGroup>>
    (`/v1/efdb/relevant-emissionfactors/category/${categoryId}/grouped/`, { params })
  },
  listOtherEmissionFactors(params: OtherEmissionFactorsParams) {
    const { query, category, unit, page, signal } = params
    return axios.get(`/v1/efdb/other-emissionfactors/?search=${query}&category=${category}&unit=${unit}&page_size=5&page=${page}`, { signal })
  },
  listRegions() {
    return axios.get('/v1/efdb/regions/')
  },
  getSubmissionSupportingDocs(previousSubmissionId: number | string) {
    return axios.get(`/v1/efdb/data-approval-get-supporting-docs-item-names/submission/${previousSubmissionId}/`)
  },
  getCategorySupportingDocs(categoryId: number | string, batchId: number | string) {
    return axios.get(`/v1/efdb/get-supporting-docs-item-names/category/${categoryId}/?batch_id=${batchId}`)
  },

  getRelevantEmissionFactors(categoryId: number | string, queryString: number | string, token?: tokenData) {
    return axios.get(`/v1/efdb/relevant-emissionfactors/category/${categoryId}/grouped/${queryString}`, token)
  },

  convertDraftEmissions(categoryId: number | string, queryString: number | string) {
    return axios.put(`/v1/efdb/convert-draft-emissions/category/${categoryId}/${queryString}`)
  },
  saveGroupedEmissionData(emissionData: EditEfMatchingGroupPayload | EditEfMatchingGroupPayload[], isSingle: boolean = true) {
    return axios.put(`/v1/efdb/save-grouped-emissiondata/?single=${isSingle}`, emissionData)
  },
  otherEmissionFactors(queryString: string = '', tokenData: tokenData) {
    return axios.get(`/v1/efdb/other-emissionfactors/${queryString}`, tokenData)
  },
  getTotalEmissionsPreview(categoryId: number | string, queryString: string = '') {
    return axios.get(`/v1/efdb/get-draft-total-emissions-preview/category/${categoryId}/${queryString}`)
  },

  createRegion(data: RegionData) {
    return axios.put(`/v1/efdb/regions/`, data)
  },

  updateRegion(code: number | string, data: RegionData) {
    return axios.put(`/v1/efdb/regions/${code}/`, data)
  },

  deleteRegion(code: number | string) {
    return axios.put(`/v1/efdb/regions/${code}/`)
  },

  deleteActivity(id: number) {
    return axios.delete(`/v1/efdb/activities/${id}/`)
  },
  getEfdbActivities(page: number, pageSize: number, searchQuery?: string) {
    const endpoint = `/v1/efdb/activities?page=${page}&page_size=${pageSize}${searchQuery ? `&search=${searchQuery}` : ''}`
    return axios.get(endpoint)
  },
  efdbBulkUpload(formData: FormData) {
    return axios.post(`/v1/tasks/efdb-bulk-upload/?target_model=ef_activities`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }, // Optional, usually auto-set
    })
  },
  getActivityFactor(currentPage: number, filterQuery: string) {
    return axios.get(`/v1/efdb/activity-factors/?page=${currentPage}${filterQuery}`)
  },
  getCalculation() {
    return axios.get(`/v1/efdb/get-calculation-methods/`)
  },
  getSource() {
    return axios.get(`/v1/efdb/sources/`)
  },
  getSector() {
    return axios.get(`/v1/efdb/sectors/`)
  },

  getActivities() {
    return axios.get(`/v1/efdb/activities/?paginate=false`)
  },
  createBulkUpload(importedData: EmissionFactorPayload) {
    return axios.post(`/v1/efdb/bulk/bulk-create/`, importedData)
  },
  exportEmissionFactor() {
    return axios.post(`/v1/efdb/export-excel-emission-factors/`)
  },
  deleteFactor(id: number | string) {
    return axios.delete(`/v1/efdb/${id}/`)
  },
  updateBulkUpload(formData: FormData) {
    return axios.post(`/v1/tasks/efdb-bulk-upload/?target_model=emission_factors`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }, // Optional, usually auto-set
    })
  },
  getEfdb(efid: number | string) {
    return axios.get(`/v1/efdb/${efid}/`)
  },
  editEfdb(efid: number | string, updatedDetails: updateDetailPayload) {
    return axios.put(`/v1/efdb/${efid}/edit/`, updatedDetails)
  },
  deleteSource(sourceId: string | number) {
    return axios.delete(`/v1/efdb/sources/${sourceId}/`)
  },
  updateSource(sourceId: string | number, data: UpdatedSourcePayload) {
    return axios.put(`/v1/efdb/sources/${sourceId}/`, data)
  },
  createSource(sourceData: SourcePayload) {
    return axios.post(`/v1/backstage/efsources/?subdomains=all`, sourceData)
  },
})
